import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Field } from '@app/interfaces';
import { BolhaLocationApiService, BolhaPlace } from '@app/services/api/integrations/bolha-location-api.service';
import { forkJoin, of, switchMap } from 'rxjs';
import { croatianLocationLabel } from '@app/shared/briefd-form/field-types/bolha-location-labels';

@Component({
    selector: 'app-form-input-bolha-location-v2',
    templateUrl: './form-input-bolha-location-v2.component.html',
    styleUrls: ['./form-input-bolha-location-v2.component.scss'],
    standalone: false,
})
export class FormInputBolhaLocationV2Component implements OnInit {
    @Input() field: Field<string>;
    @Input() control: FormControl;

    regions: string[] = [];
    cities: string[] = [];
    places: BolhaPlace[] = [];

    selectedRegion = '';
    selectedCity = '';
    selectedPlace: string | null = null;

    isLoadingRegions = true;
    isLoadingCities = false;
    isLoadingPlaces = false;

    constructor(
        private bolhaLocationApi: BolhaLocationApiService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.loadRegions();
    }

    displayName(name: string): string {
        return croatianLocationLabel(name);
    }

    private loadRegions(): void {
        this.isLoadingRegions = true;
        this.bolhaLocationApi.getRegions().subscribe({
            next: (regions) => {
                this.regions = regions;
                this.isLoadingRegions = false;

                if (this.field.value) {
                    this.restoreFromValue();
                }

                this.cdr.detectChanges();
            },
            error: () => {
                this.isLoadingRegions = false;
                this.cdr.detectChanges();
            },
        });
    }

    private parseBolhaId(): number | null {
        if (typeof this.field.value === 'object' && this.field.value !== null) {
            const key = Object.keys(this.field.value)[0];
            return parseInt(key?.replace(/^_/, ''), 10) || null;
        }

        if (typeof this.field.value === 'string') {
            return parseInt(this.field.value.replace(/^_/, ''), 10) || null;
        }

        return null;
    }

    private restoreFromValue(): void {
        const bolhaId = this.parseBolhaId();
        if (!bolhaId) return;

        this.bolhaLocationApi.getLocation(bolhaId).pipe(
            switchMap(location => forkJoin({
                cities: this.bolhaLocationApi.getCities(location.region),
                places: this.bolhaLocationApi.getPlaces(location.city, location.region),
                location: of(location),
            })),
        ).subscribe(({ cities, places, location }) => {
            this.selectedRegion = location.region;
            this.cities = cities;
            this.selectedCity = location.city;
            this.places = places;
            this.selectedPlace = `_${location.bolha_id}`;
            this.updateControlValue();
            this.cdr.detectChanges();
        });
    }

    onRegionChange(event: Event): void {
        const region = (event.target as HTMLSelectElement).value;
        this.selectedRegion = region;
        this.selectedCity = '';
        this.selectedPlace = null;
        this.cities = [];
        this.places = [];
        this.updateControlValue();

        if (!region) return;

        this.isLoadingCities = true;
        this.bolhaLocationApi.getCities(region).subscribe({
            next: (cities) => {
                this.cities = cities;
                this.isLoadingCities = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.isLoadingCities = false;
                this.cdr.detectChanges();
            },
        });
    }

    onCityChange(event: Event): void {
        const city = (event.target as HTMLSelectElement).value;
        this.selectedCity = city;
        this.selectedPlace = null;
        this.places = [];
        this.updateControlValue();

        if (!city) return;

        this.isLoadingPlaces = true;
        this.bolhaLocationApi.getPlaces(city, this.selectedRegion).subscribe({
            next: (places) => {
                this.places = places;
                this.isLoadingPlaces = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.isLoadingPlaces = false;
                this.cdr.detectChanges();
            },
        });
    }

    onPlaceChange(event: Event): void {
        this.selectedPlace = (event.target as HTMLSelectElement).value || null;
        this.updateControlValue();
    }

    private updateControlValue(): void {
        if (this.selectedPlace) {
            const place = this.places.find((p) => `_${p.bolha_id}` === this.selectedPlace);
            if (place) {
                this.control.setValue({ [this.selectedPlace]: place.name });
                this.control.setErrors(null);
            } else {
                this.control.setValue(null);
            }
        } else {
            this.control.setValue(null);

            // If user started selecting (picked a region) but didn't finish, mark as invalid
            if (this.selectedRegion) {
                this.control.setErrors({ incompleteLocation: true });
            } else {
                this.control.setErrors(null);
            }
        }
        this.cdr.detectChanges();
    }
}